<template>
  <div v-if="showLoader" class="loader-container" :class="containerClasses">
    <div class="dots-wave">
      <div class="dot" :style="{ animationDelay: '0ms' }"></div>
      <div class="dot" :style="{ animationDelay: '150ms' }"></div>
      <div class="dot" :style="{ animationDelay: '300ms' }"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DotsWaveLoader',
  props: {
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    color: {
      type: String,
      default: '#3b82f6' // Default blue color
    },
    visible: {
      type: Boolean,
      default: true
    },
    delayMs: {
      type: Number,
      default: 150 // Delay before showing loader
    },
    minDurationMs: {
      type: Number,
      default: 500 // Minimum time to show loader
    }
  },
  data() {
    return {
      showLoader: false,
      startTime: null,
      delayTimeout: null,
      isLoaderShown: false
    };
  },
  computed: {
    containerClasses() {
      return {
        [`loader-${this.size}`]: true
      };
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.startLoading();
        } else {
          this.stopLoading();
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    if (this.delayTimeout) {
      clearTimeout(this.delayTimeout);
    }
  },
  methods: {
    startLoading() {
      this.startTime = Date.now();
      
      // Clear any existing timeout
      if (this.delayTimeout) {
        clearTimeout(this.delayTimeout);
      }
      
      // Show loader after delay
      this.delayTimeout = setTimeout(() => {
        this.showLoader = true;
        this.isLoaderShown = true;
      }, this.delayMs);
    },
    
    async stopLoading() {
      // Clear delay timeout if loading finishes before delay
      if (this.delayTimeout) {
        clearTimeout(this.delayTimeout);
        this.delayTimeout = null;
      }
      
      // If loader was shown, ensure minimum duration
      if (this.isLoaderShown) {
        const elapsed = Date.now() - this.startTime;
        if (elapsed < this.minDurationMs) {
          await new Promise(resolve => 
            setTimeout(resolve, this.minDurationMs - elapsed)
          );
        }
      }
      
      // Hide loader
      this.showLoader = false;
      this.isLoaderShown = false;
    }
  },
  mounted() {
    // Apply custom color if provided
    if (this.color !== '#3b82f6') {
      this.$nextTick(() => {
        const dots = this.$el?.querySelectorAll('.dot');
        dots?.forEach(dot => {
          dot.style.backgroundColor = this.color;
        });
      });
    }
  }
};
</script>

<style scoped>
/* Base container styles - centered within parent */
.loader-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  z-index: 100;
}

/* Dots container */
.dots-wave {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Individual dot */
.dot {
  border-radius: 50%;
  background-color: var(--main-color);
  animation: wave 1.2s ease-in-out infinite;
}

/* Wave animation */
@keyframes wave {
  0%, 40%, 100% {
    transform: translateY(0);
    opacity: 0.8;
  }
  20% {
    transform: translateY(-12px);
    opacity: 1;
  }
}

.loader-small .dot {
  width: 8px;
  height: 8px;
}

.loader-medium .dot {
  width: 12px;
  height: 12px;
}

.loader-large .dot {
  width: 16px;
  height: 16px;
}

.loader-large .dots-wave {
  gap: 0.75rem;
}

.loader-large .dot {
  animation-name: wave-large;
}

@keyframes wave-large {
  0%, 40%, 100% {
    transform: translateY(0);
    opacity: 0.8;
  }
  20% {
    transform: translateY(-16px);
    opacity: 1;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .dot {
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }
}
</style>